import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, Table, Collapse, Row, Col, Form } from "react-bootstrap";
import api from "../api/api";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { MapContainer, TileLayer, Polygon } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import ndviIcon from "../assets/images/NDVI.png";
import ndwiIcon from "../assets/images/NDWI.png";
import swirIcon from "../assets/images/SWIR.png";
import moistureIcon from "../assets/images/MOISTURE-INDEX.png";
import Swal from "sweetalert2";
import moment from "moment";
import { useTranslation } from "react-i18next";

moment.locale("en");
const designationImageMap = {
  ndvi: "vegetation_health",
  ndwi: "irrigation_index",
  moisture: "vegetation_moisture",
  swir: "water_stress",
};
dayjs.extend(isoWeek);

export default function FieldDetails() {
  const { t } = useTranslation();
  const { fieldId } = useParams();
  const [field, setField] = useState(null);
  const [imagesByWeek, setImagesByWeek] = useState({});
  const [openWeeks, setOpenWeeks] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchField = async () => {
      try {
        const res = await api.post("/field", { field_uid: fieldId });
        setField(res.data.field);
        setImagesByWeek(res.data.imagesByWeek);
      } catch (error) {
        console.error(t("error_loading_field"), error);
      } finally {
        setLoading(false);
      }
    };
    fetchField();
  }, [fieldId, t]);

  useEffect(() => {
    const now = new Date();
    setSelectedMonth(now.getMonth());
    setSelectedYear(now.getFullYear());
  }, []);

  const toggleWeek = (weekKey) => {
    setOpenWeeks((prev) => ({
      ...prev,
      [weekKey]: !prev[weekKey],
    }));
  };

  const filteredWeekKeys = Object.keys(imagesByWeek)
    .filter((key) => {
      const [yearStr, weekStr] = key.split("-");
      const date = moment(`${yearStr}-W${weekStr}`, "YYYY-[W]WW");
      const matchYear = selectedYear ? +yearStr === +selectedYear : true;
      const matchMonth = selectedMonth !== null && selectedMonth !== ""
        ? date.month() === parseInt(selectedMonth)
        : true;
      return matchYear && matchMonth;
    })
    .sort();

  const formatWeekRange = (weekKey) => {
    const [year, week] = weekKey.split("-W").map(Number);
    const startOfWeek = dayjs().year(year).isoWeek(week).startOf("week");
    const endOfWeek = dayjs().year(year).isoWeek(week).endOf("week");
    return `${startOfWeek.format("MMM D, YYYY")} – ${endOfWeek.format(
      "MMM D, YYYY"
    )}`;
  };

  const getTypeIcon = (type) => {
    const lower = type?.toLowerCase();
    switch (lower) {
      case "ndvi":
        return ndviIcon;
      case "ndwi":
        return ndwiIcon;
      case "swir":
        return swirIcon;
      case "moisture":
        return moistureIcon;
      default:
        return null;
    }
  };

  const isValidCoordinate = (lat, lng) =>
    !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng));

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "300px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4" style={{ overflowX: "hidden" }}>
      {field && (
        <Card className="mb-4 shadow">
          <Card.Header className="bg-success text-white">
            <h5 className="mb-0 text-white">🌿 {t("field_overview")}</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col xs={12} md={5}>
                <dl className="row small">
                  <dt className="col-5">{t("field_name")}</dt>
                  <dd className="col-7">{field.name}</dd>
                  <dt className="col-5">{t("status")}</dt>
                  <dd className="col-7">{field.status}</dd>
                  <dt className="col-5">{t("created_at")}</dt>
                  <dd className="col-7">
                    {new Date(field.created_at).toLocaleDateString()}
                  </dd>
                  <dt className="col-5">{t("dimensions")}</dt>
                  <dd className="col-7">
                    {field.longueur || "?"} × {field.largeur || "?"} {t("meters")}
                  </dd>
                  <dt className="col-5">{t("zone")}</dt>
                  <dd className="col-7">{field.zones?.[0]?.name || t("na")}</dd>
                  <hr className="my-2" />
                  <dt className="col-5">{t("farm")}</dt>
                  <dd className="col-7">{field.farm?.name || t("na")}</dd>
                  <dt className="col-5">{t("owner")}</dt>
                  <dd className="col-7">{field.farm?.user?.name || t("na")}</dd>
                  <dt className="col-5">{t("city")}</dt>
                  <dd className="col-7">{field.farm?.city?.city || t("na")}</dd>
                  <dt className="col-5">{t("country")}</dt>
                  <dd className="col-7">{field.farm?.city?.country || t("na")}</dd>
                </dl>
              </Col>
              <Col xs={12} md={7}>
                <div
                  style={{
                    height: "300px",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  {isValidCoordinate(field.Latitude, field.Longitude) ? (
                    <MapContainer
                      center={[
                        parseFloat(field.Latitude),
                        parseFloat(field.Longitude),
                      ]}
                      zoom={17}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom={true}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      {field.coordinates && (
                        <Polygon
                          positions={JSON.parse(field.coordinates).map((p) => [
                            p.Lat,
                            p.Long,
                          ])}
                          pathOptions={{ color: "#198754", weight: 2 }}
                        />
                      )}
                    </MapContainer>
                  ) : (
                    <div className="text-danger text-center p-4">
                      <i className="fas fa-triangle-exclamation" aria-hidden="true"></i>{" "}
                      {t("invalid_coordinates")}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      <Row className="mb-3 justify-content-center gap-2">
        <Col xs={6} sm={4} md={3}>
          <Form.Label>{t("select_month")}</Form.Label>
          <Form.Select
            value={selectedMonth ?? ""}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">{t("all_months")}</option>
            {moment.months().map((month, i) => (
              <option key={i} value={i}>
                {month}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6} sm={4} md={3}>
          <Form.Label>{t("select_year")}</Form.Label>
          <Form.Select
            value={selectedYear ?? ""}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="">{t("all_years")}</option>
            {[
              ...new Set(
                Object.keys(imagesByWeek).map((key) => key.split("-")[0])
              ),
            ].map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      <Card>
        <Card.Header>
          <h5>{t("weekly_satellite_images")}</h5>
        </Card.Header>
        <Card.Body>
          <Table bordered className="text-center align-middle">
            <thead className="bg-light">
              <tr>
                <th style={{ width: "50%" }}>{t("week")}</th>
                <th>{t("action")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredWeekKeys?.length > 0 ? (
                filteredWeekKeys?.sort().map((weekKey) => (
                  <React.Fragment key={weekKey}>
                    <tr>
                      <td>{formatWeekRange(weekKey)}</td>
                      <td className="d-flex flex-wrap gap-2 justify-content-center">
                        <Button
                          variant="primary"
                          onClick={() => toggleWeek(weekKey)}
                          size="sm"
                          style={{ width: 120 }}
                        >
                          {openWeeks[weekKey] ? t("hide_images") : t("show_images")}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className=""
                          style={{ width: 120 }}
                          onClick={async () => {
                            const result = await Swal.fire({
                              title: t("delete_images_title", { weekKey }),
                              text: t("delete_images_confirm"),
                              icon: "warning",
                              showCancelButton: true,
                              confirmButtonColor: "#d33",
                              cancelButtonColor: "#3085d6",
                              confirmButtonText: t("delete_confirm"),
                              cancelButtonText: t("cancel"),
                            });

                            if (result.isConfirmed) {
                              try {
                                await api.post(
                                  "/api/delete-week/sattelite-images",
                                  {
                                    field_uid: fieldId,
                                    week_key: weekKey,
                                  }
                                );

                                setImagesByWeek((prev) => {
                                  const updated = { ...prev };
                                  delete updated[weekKey];
                                  return updated;
                                });

                                Swal.fire({
                                  title: t("deleted"),
                                  text: t("images_deleted", { weekKey }),
                                  icon: "success",
                                  timer: 2000,
                                  showConfirmButton: false,
                                });
                              } catch (err) {
                                console.error(err);
                                Swal.fire(
                                  t("error"),
                                  t("failed_delete_images"),
                                  "error"
                                );
                              }
                            }
                          }}
                        >
                          {t("delete_week")}
                        </Button>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" className="p-3">
                        <Collapse in={openWeeks[weekKey]}>
                          <div
                            style={{
                              maxHeight: "300px",
                              overflowY: "auto",
                              overflowX: "hidden",
                            }}
                          >
                            <Table
                              bordered
                              size="sm"
                              className="mb-0"
                              style={{
                                tableLayout: "fixed",
                                width: "100%",
                              }}
                            >
                              <thead>
                                <tr>
                                  <th>{t("image")}</th>
                                  <th>{t("date")}</th>
                                  <th>{t("type")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(imagesByWeek[weekKey] || []).map((img) => (
                                  <tr key={img.id}>
                                    <td>
                                      <img
                                        src={`${process.env.REACT_APP_BASE_URL}${img.image_url}`}
                                        alt={t("satellite_image")}
                                        className="img-fluid"
                                        style={{
                                          maxWidth: "100%",
                                          width: "150px",
                                          height: "auto",
                                          borderRadius: "4px",
                                        }}
                                      />
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      {new Date(
                                        img.start_date
                                      ).toLocaleDateString()}
                                    </td>
                                    <td style={{ verticalAlign: "middle" }}>
                                      {img.type ? (
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                          <img
                                            src={getTypeIcon(img.type)}
                                            alt={t(img.type.toLowerCase())}
                                            style={{
                                              width: "24px",
                                              height: "24px",
                                              objectFit: "contain",
                                              borderRadius: "50%",
                                            }}
                                          />
                                          <div className="text-start">
                                            <div className="fw-bold">
                                              {img.type.toUpperCase()}
                                            </div>
                                            <div className="text-muted small">
                                              {t(
                                                designationImageMap[
                                                  img.type.toLowerCase()
                                                ] || "na"
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ) : (
                                        t("na")
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="2" className="text-center text-muted">
                    {t("no_satellite_images")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
}